/**
 * Helper WebAuthn — encode/decode base64url + appels navigator.credentials.
 *
 * Le serveur Python (lib `webauthn`) sérialise les bytes en base64url. Le
 * navigateur attend des ArrayBuffer/Uint8Array. Ces helpers font la conversion
 * dans les deux sens.
 */

export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && typeof navigator.credentials !== 'undefined'
    && typeof navigator.credentials.create === 'function';
}

export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await (window.PublicKeyCredential as any)
      .isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** base64url (sans padding) → ArrayBuffer. */
export function b64urlToBuf(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
}

/** ArrayBuffer/Uint8Array → base64url (sans padding). */
export function bufToB64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Convertit les options renvoyées par le backend (champs base64url) en
 *  PublicKeyCredentialCreationOptions consommables par navigator.credentials.create. */
export function decodeCreationOptions(opts: any): PublicKeyCredentialCreationOptions {
  return {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    user: {
      ...opts.user,
      id: b64urlToBuf(opts.user.id),
    },
    excludeCredentials: (opts.excludeCredentials || []).map((c: any) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };
}

export function decodeRequestOptions(opts: any): PublicKeyCredentialRequestOptions {
  return {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c: any) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };
}

/** Encode un PublicKeyCredential (create) en JSON pour le backend. */
export function encodeAttestationCredential(cred: PublicKeyCredential): any {
  const resp = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      attestationObject: bufToB64url(resp.attestationObject),
      transports: typeof (resp as any).getTransports === 'function'
        ? (resp as any).getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

/** Encode un PublicKeyCredential (get) en JSON pour le backend. */
export function encodeAssertionCredential(cred: PublicKeyCredential): any {
  const resp = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      authenticatorData: bufToB64url(resp.authenticatorData),
      signature: bufToB64url(resp.signature),
      userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}
